import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicCustomFormsAPI } from '../services/api';
import { useToast } from '../context/ToastContext';

/**
 * DynamicPrivacy - Project-specific Privacy Policy page
 * Route: /privacidad/:slug
 * 
 * Falls back to default Privacy content if no project-specific content exists
 */
const DynamicPrivacy = () => {
  const { slug } = useParams();
  const toast = useToast();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProjectSpecific, setIsProjectSpecific] = useState(false);
  const [projectName, setProjectName] = useState('');

  useEffect(() => {
    loadPrivacyContent();
  }, [slug]);

  const loadPrivacyContent = async () => {
    if (!slug) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await publicCustomFormsAPI.getDocument(slug, 'privacy');
      if (response.data && response.data.content) {
        setContent(response.data.content);
        setIsProjectSpecific(true);
        if (response.data.projectName) {
          setProjectName(response.data.projectName);
        }
      } else {
        setIsProjectSpecific(false);
      }
    } catch (error) {
      console.log('No custom privacy found for this project, using default');
      setIsProjectSpecific(false);
    } finally {
      setLoading(false);
    }
  };

  // Default privacy content (same as Privacy.js)
  const defaultPrivacy = (
    <div className="prose max-w-none text-gray-700 space-y-6">
      <p className="text-sm text-gray-500">
        Conforme a la Ley N° 29733 - Ley de Protección de Datos Personales y su Reglamento (D.S. N° 016-2024-JUS)
      </p>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Identidad y Domicilio del Responsable</h2>
        <p>
          El responsable del tratamiento de sus datos personales es el organizador del evento, 
          cuyos datos de contacto se indican en el formulario de registro. El domicilio 
          se encuentra en Lima, Perú.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Finalidad del Tratamiento</h2>
        <p>Sus datos personales serán tratados para las siguientes finalidades:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Registro y gestión de asistentes al evento</li>
          <li>Generación de códigos QR de acceso</li>
          <li>Control de ingreso al evento</li>
          <li>Comunicación relativa al evento (confirmaciones, recordatorios)</li>
          <li><strong>Opcional:</strong> Reconocimiento facial para check-in automatizado</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Datos Personales Recopilados</h2>
        <p>Los datos que recopilamos incluyen:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Nombre completo</li>
          <li>Correo electrónico</li>
          <li>Teléfono</li>
          <li>Empresa / Organización</li>
          <li>Fotografía (para el pase de acceso)</li>
          <li><strong>Opcional:</strong> Descriptor facial (para reconocimiento facial)</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Tratamiento de Datos Biométricos</h2>
        <p className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <strong>IMPORTANTE:</strong> El reconocimiento facial es <strong>completamente opcional</strong>. 
          Você puede registrarse y acceder al evento sin necesidad de utilizar el reconocimiento facial.
        </p>
        <p className="mt-3">Si usted opta por utilizar el reconocimiento facial:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Su descriptor facial será encriptado antes de ser almacenado</li>
          <li>Solo se utilizará para verificación de identidad en el ingreso al evento</li>
          <li>Será eliminado automáticamente después de finalizada la duración del evento</li>
          <li>No será compartido con terceros</li>
          <li>No se utilizará para ningún otro propósito</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Tiempo de Conservación</h2>
        <p>Sus datos personales serán conservados durante los siguientes períodos:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><strong>Datos de registro:</strong> Duración del evento + 30 días</li>
          <li><strong>Datos biométricos:</strong> Duración del evento (eliminación automática)</li>
          <li><strong>Fotos del mosaico:</strong> Duración del evento</li>
          <li><strong>Logs de acceso:</strong> 1 año (con anonimización)</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Transferencia de Datos</h2>
        <p>
          Sus datos personales no serán transferidos a terceros, salvo requerimiento 
          legal o autorización expresa. No vendemos ni alquilamos sus datos personales.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Medidas de Seguridad</h2>
        <p>Implementamos medidas de seguridad técnicas y organizativas para proteger sus datos:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Encriptación de datos biométricos</li>
          <li>Conexiones seguras (HTTPS/TLS)</li>
          <li>Control de acceso basado en roles</li>
          <li>Monitoreo de accesos</li>
          <li>Respaldo seguro de información</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Sus Derechos ARCO</h2>
        <p>
          Conforme a la Ley N° 29733, usted tiene los siguientes derechos:
        </p>
        <div className="mt-4 space-y-3">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-blue-900">📖 Derecho de Acceso</h3>
            <p className="text-blue-800 text-sm mt-1">
              Puede solicitar información sobre sus datos personales que tenemos almacenados, 
              incluyendo la finalidad del tratamiento y terceros con quienes se comparten.
            </p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="font-semibold text-green-900">✏️ Derecho de Rectificación</h3>
            <p className="text-green-800 text-sm mt-1">
              Puede solicitar la corrección o actualización de datos inexactos, incompletos 
              o desactualizados.
            </p>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg">
            <h3 className="font-semibold text-red-900">🗑️ Derecho de Cancelación</h3>
            <p className="text-red-800 text-sm mt-1">
              Puede solicitar la eliminación de sus datos cuando: (a) haya vencido el plazo 
              de conservación, (b) haya revocado su consentimiento, o (c) los datos ya 
              no sean necesarios para la finalidad pour la cual fueron recopilados.
            </p>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="font-semibold text-yellow-900">⛔ Derecho de Oposición</h3>
            <p className="text-yellow-800 text-sm mt-1">
              Puede oponerse al tratamiento de sus datos cuando tenga motivos legítimos 
              relacionados con una situación personal concreta.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Cómo Ejercer sus Derechos</h2>
        <p>Para ejercer sus derechos ARCO, puede:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Contactar directamente al organizador del evento</li>
          <li>Enviar una solicitud al correo electrónico proporcionado en el registro</li>
          <li>
            Presentar un reclamo ante la Autoridad Nacional de Protección de Datos Personales (ANPD)
            si no obtiene respuesta en el plazo establecido.
          </li>
        </ul>
        <p className="mt-3 text-sm text-gray-600">
          <strong>Plazos:</strong> Derecho de Acceso: 20 días hábiles | 
          Derechos de Rectificación, Cancelación y Oposición: 10 días hábiles
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Consentimiento</h2>
        <p>
          Al completar el formulario de registro, usted:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Manifiesta haber leído y aceptado el presente Aviso de Privacidad</li>
          <li>Autoriza el tratamiento de sus datos personales para las finalidades indicadas</li>
          <li>Para datos biométricos, otorga su consentimiento expreso de manera separada</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Información de Contacto</h2>
        <p>
          Para consultas sobre el tratamiento de sus datos o ejercer sus derechos ARCO, 
          contacte al organizador del evento o a la Autoridad Nacional de Protección 
          de Datos Personales:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><strong>ANPD:</strong> www.gob.pe/anpd</li>
          <li><strong>Teléfono:</strong> (01) 204 8020</li>
          <li><strong>Correo:</strong> protegetusdatos@minjus.gob.pe</li>
        </ul>
      </section>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 flex items-center justify-center">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          {isProjectSpecific && projectName 
            ? `Política de Privacidad - ${projectName}`
            : 'Aviso de Privacidad y Tratamiento de Datos'
          }
        </h1>
        
        {isProjectSpecific && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Nota:</strong> Esta es la política de privacidad específica para el evento al que te estás registrando.
              También aplica nuestra política general de privacidad.
            </p>
          </div>
        )}

        {isProjectSpecific && content ? (
          <div 
            className="prose max-w-none text-gray-700"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : (
          defaultPrivacy
        )}

        <div className="mt-8 pt-6 border-t border-gray-200">
          <Link to="/" className="text-blue-600 hover:text-blue-800">
            ← Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DynamicPrivacy;
